// ============================================
// pages/Salon/SalonBilling.js
// Multi-Bill Management System with Stations
// ============================================
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../common/context/AuthContext';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { SALON_API_URL } from '../../api/config';
import {
  ChevronRight, ChevronLeft, Check, X, Plus, Minus,
  Scissors, Package, Users, Search, CreditCard, Banknote,
  Smartphone, Loader2, Trash2, User, Phone, ChevronDown,
  ReceiptText, CheckCircle, SplitSquareVertical, Edit2,
  Save, Clock, Armchair, Bed, Home, Grid3x3, Settings, ShowerHead, Sparkles,
} from 'lucide-react';

// ─── Constants ─────────────────────────────────────────────────────────────
const GENDER_TABS = ['All', 'Men', 'Women'];
const PAYMENT_OPTIONS = [
  { id: 'cash',  label: 'Cash',  Icon: Banknote },
  { id: 'upi',   label: 'UPI',   Icon: Smartphone },
  { id: 'card',  label: 'Card',  Icon: CreditCard },
  { id: 'split', label: 'Split', Icon: SplitSquareVertical },
];

const STATION_ICONS = {
  chair: Armchair,
  shampoo: ShowerHead,
  room: Home,
  manipedi: Sparkles,
};

const STATION_LABELS = {
  chair: 'Chair',
  shampoo: 'Shampoo Station',
  room: 'Room',
  manipedi: 'Mani-pedi Station',
};

// ─── Helpers ───────────────────────────────────────────────────────────────
function fmt(n) {
  return '₹' + Number(n).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}
function getInitials(name = '') {
  return name.trim().split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
}
function avatarBg(name = '') {
  const c = ['bg-violet-500','bg-blue-500','bg-emerald-500','bg-rose-500','bg-amber-500','bg-cyan-500','bg-pink-500'];
  return c[(name.charCodeAt(0) || 0) % c.length];
}

// ─── Steps indicator (2 or 3 steps based on products toggle) ──────────────
function Steps({ current, onStepClick, includeProducts }) {
  const steps = includeProducts 
    ? [
        { n:1, label:'Services', Icon:Scissors },
        { n:2, label:'Products', Icon:Package  },
        { n:3, label:'Details',  Icon:Users    },
      ]
    : [
        { n:1, label:'Services', Icon:Scissors },
        { n:2, label:'Details',  Icon:Users    },
      ];
  
  return (
    <div className="flex items-center gap-0 mb-6">
      {steps.map((s, i) => {
        const done   = s.n < current;
        const active = s.n === current;
        const clickable = done;
        return (
          <React.Fragment key={s.n}>
            <div className="flex flex-col items-center gap-1 flex-shrink-0">
              <button
                onClick={() => clickable && onStepClick(s.n)}
                disabled={!clickable}
                className={`w-9 h-9 rounded-xl flex items-center justify-center font-black text-sm transition-all ${
                  done
                    ? 'bg-purple-600 text-white cursor-pointer hover:bg-purple-700 hover:scale-105'
                    : active
                    ? 'bg-white dark:bg-slate-800 text-purple-600 border-2 border-purple-600 shadow-md'
                    : 'bg-gray-100 dark:bg-slate-800 text-gray-400 cursor-not-allowed'
                }`}
              >
                {done ? <Check size={16}/> : <s.Icon size={16}/>}
              </button>
              <span className={`text-xs font-semibold hidden sm:block ${active?'text-purple-600':done?'text-purple-400':'text-gray-400'}`}>
                {s.label}
              </span>
            </div>
            {i < steps.length-1 && (
              <div className={`flex-1 h-0.5 mx-1 mt-[-10px] sm:mt-[-18px] transition-all ${s.n < current?'bg-purple-400':'bg-gray-200 dark:bg-slate-700'}`}/>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── Staff picker ──────────────────────────────────────────────────────────
function StaffPicker({ label, value, onChange, staffList, excludeId, placeholder, optional }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef();
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const filtered = staffList.filter(s => s.id !== excludeId && s.name.toLowerCase().includes(search.toLowerCase()));
  const selected = staffList.find(s => s.id === value);
  return (
    <div className="relative" ref={ref}>
      {label && <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wide">{label} {optional && <span className="normal-case font-normal text-gray-400">(opt)</span>}</label>}
      <button onClick={() => setOpen(o=>!o)}
        className="w-full flex items-center gap-2 px-3 py-2.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-sm text-left hover:border-purple-300 transition-colors">
        {selected ? (
          <>
            <div className={`w-6 h-6 rounded-lg ${avatarBg(selected.name)} text-white text-xs font-black flex items-center justify-center flex-shrink-0`}>{getInitials(selected.name)}</div>
            <span className="flex-1 font-medium text-gray-800 dark:text-white truncate">{selected.name}</span>
          </>
        ) : (
          <><User size={14} className="text-gray-400 flex-shrink-0"/><span className="flex-1 text-gray-400">{placeholder||'Select...'}</span></>
        )}
        <ChevronDown size={14} className={`text-gray-400 flex-shrink-0 transition-transform ${open?'rotate-180':''}`}/>
      </button>
      {open && (
        <div className="absolute z-30 top-full mt-1 left-0 right-0 bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-700 rounded-xl shadow-xl overflow-hidden">
          <div className="p-2 border-b border-gray-100 dark:border-slate-700">
            <div className="relative">
              <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"/>
              <input autoFocus
                className="w-full pl-7 pr-3 py-1.5 bg-gray-50 dark:bg-slate-700 rounded-lg text-xs text-gray-800 dark:text-white placeholder-gray-400 focus:outline-none"
                placeholder="Search..." value={search} onChange={e=>setSearch(e.target.value)}/>
            </div>
          </div>
          <div className="max-h-48 overflow-y-auto">
            {value && (
              <button onClick={()=>{onChange(null);setOpen(false);}}
                className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20">
                <X size={12}/> Clear
              </button>
            )}
            {filtered.length === 0
              ? <p className="text-center text-xs text-gray-400 py-4">No staff found</p>
              : filtered.map(s=>(
                <button key={s.id} onClick={()=>{onChange(s.id);setOpen(false);setSearch('');}}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors ${value===s.id?'bg-purple-50 dark:bg-purple-900/20':''}`}>
                  <div className={`w-7 h-7 rounded-lg ${avatarBg(s.name)} text-white text-xs font-black flex items-center justify-center flex-shrink-0`}>{getInitials(s.name)}</div>
                  <div className="text-left flex-1 min-w-0">
                    <p className="text-xs font-semibold text-gray-800 dark:text-white truncate">{s.name}</p>
                    <p className="text-xs text-gray-400">{s.skill_level}</p>
                  </div>
                  {value===s.id && <Check size={12} className="text-purple-600 flex-shrink-0"/>}
                </button>
              ))
            }
          </div>
        </div>
      )}
    </div>
  );
}

// ─── STEP 1: Services ──────────────────────────────────────────────────────
function Step1Services({ cartItems, onAdd, onRemove, onOpenPackageBuilder }) {
  const { userToken: token } = useAuth();
  const [services, setServices] = useState([]);
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gender, setGender] = useState('All');
  const [category, setCategory] = useState(null);
  const [typeModal, setTypeModal] = useState(null);
  const [globalSearch, setGlobalSearch] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [servicesRes, billsRes] = await Promise.all([
          fetch(`${SALON_API_URL}/services`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${SALON_API_URL}/bills?limit=150`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        const [servicesData, billsData] = await Promise.all([servicesRes.json(), billsRes.json()]);
        if (servicesData.success) setServices(servicesData.data || []);
        if (billsData.success) setBills(billsData.data || []);
      } catch {
        toast.error('Could not load services or usage statistics');
      } finally {
        setLoading(false);
      }
    };
    if (token) load();
  }, [token]);

  useEffect(() => { setCategory(null); }, [gender]);
  useEffect(() => { if (globalSearch.trim()) setCategory(null); }, [globalSearch]);

  const genderSvcs = gender === 'All'
    ? services
    : services.filter(s => s.gender === gender || s.gender === 'Unisex');

  const categories = [...new Set(genderSvcs.map(s => s.category))].filter(Boolean).sort();

  const displaySvcs = globalSearch.trim()
    ? genderSvcs.filter(s =>
        s.name.toLowerCase().includes(globalSearch.toLowerCase()) ||
        s.category?.toLowerCase().includes(globalSearch.toLowerCase())
      )
    : category
    ? genderSvcs.filter(s => s.category === category)
    : gender === 'All'
    ? []
    : genderSvcs;

  const { top10Male, top10Female } = useMemo(() => {
    const freqMap = {};
    bills.forEach((bill, index) => {
      const items = bill.items || [];
      const weight = Math.max(0.1, 1 - index * 0.005);
      items.forEach(item => {
        if (item.item_type === 'service') {
          const svcId = item.item_id;
          freqMap[svcId] = (freqMap[svcId] || 0) + (item.qty || 1) * weight;
        }
      });
    });

    const maleSvcsSorted = services
      .filter(s => s.gender === 'Men' || s.gender === 'Unisex')
      .map(s => ({ ...s, score: freqMap[s.id] || 0 }))
      .sort((a, b) => b.score - a.score || a.name.localeCompare(b.name));

    const femaleSvcsSorted = services
      .filter(s => s.gender === 'Women' || s.gender === 'Unisex')
      .map(s => ({ ...s, score: freqMap[s.id] || 0 }))
      .sort((a, b) => b.score - a.score || a.name.localeCompare(b.name));

    return {
      top10Male: maleSvcsSorted.slice(0, 10),
      top10Female: femaleSvcsSorted.slice(0, 10),
    };
  }, [services, bills]);

  const getThemeClasses = (g = gender) => {
    if (g === 'Men') {
      return {
        bg: 'bg-blue-600',
        hoverBg: 'hover:bg-blue-700',
        text: 'text-blue-650 dark:text-blue-400',
        textDark: 'text-blue-700 dark:text-blue-300',
        border: 'border-blue-400',
        hoverBorder: 'hover:border-blue-300 dark:hover:border-blue-800',
        shadow: 'shadow-blue-200 dark:shadow-blue-900/30',
        cartBg: 'bg-blue-50 dark:bg-blue-900/20',
      };
    }
    if (g === 'Women') {
      return {
        bg: 'bg-pink-600',
        hoverBg: 'hover:bg-pink-700',
        text: 'text-pink-600 dark:text-pink-400',
        textDark: 'text-pink-700 dark:text-pink-300',
        border: 'border-pink-400',
        hoverBorder: 'hover:border-pink-300 dark:hover:border-pink-800',
        shadow: 'shadow-pink-200 dark:shadow-pink-900/30',
        cartBg: 'bg-pink-50 dark:bg-pink-900/20',
      };
    }
    return {
      bg: 'bg-purple-600',
      hoverBg: 'hover:bg-purple-700',
      text: 'text-purple-600 dark:text-purple-400',
      textDark: 'text-purple-700 dark:text-purple-300',
      border: 'border-purple-400',
      hoverBorder: 'hover:border-purple-300 dark:hover:border-purple-800',
      shadow: 'shadow-purple-200 dark:shadow-purple-900/30',
      cartBg: 'bg-purple-50 dark:bg-purple-900/20',
    };
  };

  const isInCart = (svc, variant=null) => cartItems.some(i => i.item_id===svc.id && i.item_variant===(variant?.name||null));

  const handleSvcClick = (svc) => {
    const types = svc.types || [];
    if (types.length > 0) { setTypeModal({ service: svc }); return; }
    if (isInCart(svc)) onRemove(svc.id, null, 'service');
    else onAdd({ item_type:'service', item_id:svc.id, item_name:svc.name, item_variant:null, price:svc.price, qty:1 });
  };

  const handleTypeSelect = (type) => {
    const svc = typeModal.service;
    if (isInCart(svc, type)) onRemove(svc.id, type.name, 'service');
    else onAdd({ item_type:'service', item_id:svc.id, item_name:svc.name, item_variant:type.name, price:type.price, qty:1 });
  };

  if (loading) return <div className="flex items-center justify-center py-24"><Loader2 size={28} className="animate-spin text-purple-600"/></div>;

  return (
    <div className="space-y-5">
      <div className="flex flex-col lg:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-purple-400"/>
          <input
            className="w-full pl-10 pr-10 py-3 bg-purple-50 dark:bg-slate-800 border-2 border-purple-200 dark:border-purple-700 rounded-2xl text-sm text-gray-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 font-medium"
            placeholder="Search any service by name or category…"
            value={globalSearch}
            onChange={e => setGlobalSearch(e.target.value)}
          />
          {globalSearch && (
            <button onClick={() => setGlobalSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1">
              <X size={14}/>
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={onOpenPackageBuilder}
          className="w-full lg:w-auto lg:min-w-[220px] flex items-center justify-center gap-2 px-4 py-3 rounded-2xl bg-purple-600 hover:bg-purple-700 text-white text-sm font-black shadow-lg transition-all"
        >
          <Package size={16}/> Make Your Own Package
        </button>
      </div>

      {globalSearch.trim() ? (
        <div>
          <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">
            Search Results ({displaySvcs.length})
          </p>
          {displaySvcs.length === 0 ? (
            <div className="text-center py-10">
              <Search size={28} className="mx-auto text-gray-200 dark:text-slate-700 mb-2"/>
              <p className="text-sm text-gray-400">No services match "{globalSearch}"</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-2.5">
              {displaySvcs.map(svc => {
                const hasTypes = (svc.types||[]).length > 0;
                const cartCount = cartItems.filter(i=>i.item_id===svc.id).length;
                const inCart = cartCount > 0;
                const theme = getThemeClasses(svc.gender === 'Unisex' ? 'All' : svc.gender);
                return (
                  <button key={svc.id} onClick={() => handleSvcClick(svc)}
                    className={`flex items-center justify-between p-3.5 rounded-2xl border-2 transition-all text-left ${inCart?`${theme.cartBg} ${theme.border}`:`bg-white dark:bg-slate-800 border-gray-100 dark:border-slate-700 ${theme.hoverBorder}`}`}>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-bold truncate ${inCart?theme.textDark:'text-gray-800 dark:text-white'}`}>{svc.name}</p>
                      <p className="text-xs text-gray-450 mt-0.5">{svc.category} · <span className="capitalize">{svc.gender.toLowerCase()}</span></p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                      {hasTypes
                        ? <span className="text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 px-2 py-0.5 rounded-lg font-semibold">Pick type</span>
                        : <span className={`font-black text-sm ${inCart?theme.text:'text-gray-700 dark:text-white'}`}>{fmt(svc.price)}</span>
                      }
                      {inCart && !hasTypes && <div className={`w-5 h-5 rounded-full ${theme.bg} flex items-center justify-center`}><Check size={11} className="text-white"/></div>}
                      {inCart && hasTypes && <div className={`w-5 h-5 rounded-full ${theme.bg} flex items-center justify-center text-white text-xs font-black`}>{cartCount}</div>}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="flex gap-2">
            {GENDER_TABS.map(g => {
              const theme = getThemeClasses(g);
              const isActive = gender === g;
              return (
                <button key={g} onClick={()=>setGender(g)}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-all ${isActive?`${theme.bg} text-white shadow-lg ${theme.shadow}`:'bg-gray-100 dark:bg-slate-800 text-gray-650 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-slate-700'}`}>
                  {g}
                </button>
              );
            })}
          </div>

          {categories.length > 0 && (
            <div>
              <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Select Category</p>
              <div className="flex flex-wrap gap-2">
                {categories.map(cat => {
                  const isActive = category === cat;
                  const theme = getThemeClasses(gender);
                  return (
                    <button key={cat} onClick={()=>setCategory(cat===category?null:cat)}
                      className={`px-4 py-2 rounded-xl text-sm font-semibold border-2 transition-all ${isActive?`${theme.bg} text-white border-transparent`:`bg-white dark:bg-slate-800 text-gray-705 dark:text-gray-300 border-gray-200 dark:border-slate-700 ${theme.hoverBorder}`}`}>
                      {cat}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {(category || gender !== 'All') && (
            <div className="space-y-3">
              {category && <p className="text-xs font-bold text-gray-450 dark:text-slate-400 uppercase tracking-widest mb-2">{category}</p>}
              {displaySvcs.length === 0 ? (
                <p className="text-gray-400 text-sm text-center py-8">No services here</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-2.5">
                  {displaySvcs.map(svc=>{
                    const hasTypes = (svc.types||[]).length > 0;
                    const cartCount = cartItems.filter(i=>i.item_id===svc.id).length;
                    const inCart = cartCount > 0;
                    const theme = getThemeClasses(svc.gender === 'Unisex' ? gender : svc.gender);
                    return (
                      <button key={svc.id} onClick={()=>handleSvcClick(svc)}
                        className={`flex items-center justify-between p-3.5 rounded-2xl border-2 transition-all text-left ${inCart?`${theme.cartBg} ${theme.border}`:`bg-white dark:bg-slate-800 border-gray-100 dark:border-slate-700 ${theme.hoverBorder}`}`}>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-bold truncate ${inCart?theme.textDark:'text-gray-800 dark:text-white'}`}>{svc.name}</p>
                          <p className="text-xs text-gray-400 mt-0.5">{hasTypes?`${svc.types.length} variants`:`${svc.duration||'—'} min`} · <span className="capitalize">{svc.gender.toLowerCase()}</span></p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                          {hasTypes
                            ? <span className="text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 px-2 py-0.5 rounded-lg font-semibold">Pick type</span>
                            : <span className={`font-black text-sm ${inCart?theme.text:'text-gray-700 dark:text-white'}`}>{fmt(svc.price)}</span>
                          }
                          {inCart && !hasTypes && <div className={`w-5 h-5 rounded-full ${theme.bg} flex items-center justify-center`}><Check size={11} className="text-white"/></div>}
                          {inCart && hasTypes && <div className={`w-5 h-5 rounded-full ${theme.bg} flex items-center justify-center text-white text-xs font-black`}>{cartCount}</div>}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {gender === 'All' && !category && (
            <div className="space-y-6">
              {/* Top 10 Men's Services */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-4 rounded bg-blue-600"></span>
                  <p className="text-xs font-bold text-gray-450 dark:text-slate-400 uppercase tracking-widest">
                    Frequent Men's Services
                  </p>
                </div>
                {top10Male.length === 0 ? (
                  <p className="text-gray-400 text-xs py-2">No service usage history yet.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-2.5">
                    {top10Male.map(svc => {
                      const hasTypes = (svc.types||[]).length > 0;
                      const cartCount = cartItems.filter(i=>i.item_id===svc.id).length;
                      const inCart = cartCount > 0;
                      const theme = getThemeClasses('Men');
                      return (
                        <button key={svc.id} onClick={()=>handleSvcClick(svc)}
                          className={`flex items-center justify-between p-3.5 rounded-2xl border-2 transition-all text-left ${inCart?`${theme.cartBg} ${theme.border}`:`bg-white dark:bg-slate-800 border-gray-100 dark:border-slate-700 ${theme.hoverBorder}`}`}>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-bold truncate ${inCart?theme.textDark:'text-gray-800 dark:text-white'}`}>{svc.name}</p>
                            <p className="text-xs text-gray-450 mt-0.5">{hasTypes?`${svc.types.length} variants`:`${svc.duration||'—'} min`}</p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                            {hasTypes
                              ? <span className="text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 px-2 py-0.5 rounded-lg font-semibold">Pick type</span>
                              : <span className={`font-black text-sm ${inCart?theme.text:'text-gray-700 dark:text-white'}`}>{fmt(svc.price)}</span>
                            }
                            {inCart && !hasTypes && <div className={`w-5 h-5 rounded-full ${theme.bg} flex items-center justify-center`}><Check size={11} className="text-white"/></div>}
                            {inCart && hasTypes && <div className={`w-5 h-5 rounded-full ${theme.bg} flex items-center justify-center text-white text-xs font-black`}>{cartCount}</div>}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Top 10 Women's Services */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-4 rounded bg-pink-600"></span>
                  <p className="text-xs font-bold text-gray-450 dark:text-slate-400 uppercase tracking-widest">
                    Frequent Women's Services
                  </p>
                </div>
                {top10Female.length === 0 ? (
                  <p className="text-gray-400 text-xs py-2">No service usage history yet.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-2.5">
                    {top10Female.map(svc => {
                      const hasTypes = (svc.types||[]).length > 0;
                      const cartCount = cartItems.filter(i=>i.item_id===svc.id).length;
                      const inCart = cartCount > 0;
                      const theme = getThemeClasses('Women');
                      return (
                        <button key={svc.id} onClick={()=>handleSvcClick(svc)}
                          className={`flex items-center justify-between p-3.5 rounded-2xl border-2 transition-all text-left ${inCart?`${theme.cartBg} ${theme.border}`:`bg-white dark:bg-slate-800 border-gray-100 dark:border-slate-700 ${theme.hoverBorder}`}`}>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-bold truncate ${inCart?theme.textDark:'text-gray-800 dark:text-white'}`}>{svc.name}</p>
                            <p className="text-xs text-gray-455 mt-0.5">{hasTypes?`${svc.types.length} variants`:`${svc.duration||'—'} min`}</p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                            {hasTypes
                              ? <span className="text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 px-2 py-0.5 rounded-lg font-semibold">Pick type</span>
                              : <span className={`font-black text-sm ${inCart?theme.text:'text-gray-700 dark:text-white'}`}>{fmt(svc.price)}</span>
                            }
                            {inCart && !hasTypes && <div className={`w-5 h-5 rounded-full ${theme.bg} flex items-center justify-center`}><Check size={11} className="text-white"/></div>}
                            {inCart && hasTypes && <div className={`w-5 h-5 rounded-full ${theme.bg} flex items-center justify-center text-white text-xs font-black`}>{cartCount}</div>}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {typeModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl w-full max-w-sm">
            <div className="flex items-center justify-between p-5 border-b border-gray-100 dark:border-slate-800">
              <div>
                <h3 className="font-black text-gray-900 dark:text-white">{typeModal.service.name}</h3>
                <p className="text-xs text-gray-400 mt-0.5">Select a variant — can add multiple</p>
              </div>
              <button onClick={()=>setTypeModal(null)} className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-400"><X size={16}/></button>
            </div>
            <div className="p-4 space-y-2 max-h-72 overflow-y-auto">
              {typeModal.service.types.map(type=>{
                const inCart = isInCart(typeModal.service, type);
                return (
                  <button key={type.name} onClick={()=>handleTypeSelect(type)}
                    className={`w-full flex items-center justify-between p-3.5 rounded-2xl border-2 transition-all ${inCart?'bg-purple-50 dark:bg-purple-900/20 border-purple-400':'border-gray-100 dark:border-slate-700 hover:border-purple-200'}`}>
                    <span className={`font-semibold text-sm ${inCart?'text-purple-700 dark:text-purple-300':'text-gray-800 dark:text-white'}`}>{type.name}</span>
                    <div className="flex items-center gap-2">
                      <span className="font-black text-sm text-gray-700 dark:text-white">{fmt(type.price)}</span>
                      {inCart && <Check size={15} className="text-purple-600"/>}
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="p-4 pt-2">
              <button onClick={()=>setTypeModal(null)} className="w-full py-2.5 rounded-xl bg-purple-600 text-white font-bold text-sm">Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── STEP 2: Products ──────────────────────────────────────────────────────
function Step2Products({ cartItems, onAdd, onUpdateQty, onRemove }) {
  const { userToken: token } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${SALON_API_URL}/products?inStock=true`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json();
        if (data.success) setProducts(data.data);
      } catch { toast.error('Could not load products'); }
      finally { setLoading(false); }
    };
    if (token) load();
  }, [token]);

  const filtered = products.filter(p => p.name.toLowerCase().includes(search.toLowerCase()) || p.category?.toLowerCase().includes(search.toLowerCase()));
  const getCartItem = (id) => cartItems.find(i => i.item_id===id && i.item_type==='product');

  if (loading) return <div className="flex items-center justify-center py-24"><Loader2 size={28} className="animate-spin text-purple-600"/></div>;

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"/>
        <input className="w-full pl-9 pr-4 py-2.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-sm text-gray-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
          placeholder="Search products..." value={search} onChange={e=>setSearch(e.target.value)}/>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-16">
          <Package size={32} className="mx-auto text-gray-200 dark:text-slate-700 mb-3"/>
          <p className="text-sm text-gray-400 font-medium">No products in stock</p>
          <p className="text-xs text-gray-300 dark:text-slate-600 mt-1">You can skip this step</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-2">
          {filtered.map(prod=>{
            const cartItem = getCartItem(prod.id);
            const qty = cartItem?.qty || 0;
            return (
              <div key={prod.id} className={`flex items-center gap-3 p-3.5 rounded-2xl border-2 transition-all ${qty>0?'bg-purple-50 dark:bg-purple-900/20 border-purple-300 dark:border-purple-600':'bg-white dark:bg-slate-800 border-gray-100 dark:border-slate-700'}`}>
                <div className="w-12 h-12 rounded-xl overflow-hidden bg-gray-100 dark:bg-slate-700 flex-shrink-0 flex items-center justify-center">
                  {prod.image_url ? <img src={prod.image_url} alt={prod.name} className="w-full h-full object-cover"/> : <Package size={20} className="text-gray-300"/>}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-gray-800 dark:text-white truncate">{prod.name}</p>
                  <p className="text-xs text-gray-400">{prod.category} · Stock: {prod.stock_qty}</p>
                  <p className="text-sm font-black text-purple-600 dark:text-purple-400 mt-0.5">{fmt(prod.price)}</p>
                </div>
                {qty === 0 ? (
                  <button onClick={()=>onAdd({ item_type:'product', item_id:prod.id, item_name:prod.name, item_variant:null, price:prod.price, qty:1, _maxQty:prod.stock_qty })}
                    className="w-8 h-8 rounded-xl bg-purple-600 text-white flex items-center justify-center hover:bg-purple-700 flex-shrink-0">
                    <Plus size={15}/>
                  </button>
                ) : (
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={()=>qty===1?onRemove(prod.id,null,'product'):onUpdateQty(prod.id,qty-1)}
                      className="w-7 h-7 rounded-lg bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-400 flex items-center justify-center hover:bg-red-100 hover:text-red-500">
                      {qty===1 ? <Trash2 size={12}/> : <Minus size={12}/>}
                    </button>
                    <span className="w-6 text-center text-sm font-black text-purple-600 dark:text-purple-400">{qty}</span>
                    <button onClick={()=>{ if(qty<prod.stock_qty) onUpdateQty(prod.id,qty+1); }} disabled={qty>=prod.stock_qty}
                      className="w-7 h-7 rounded-lg bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 flex items-center justify-center hover:bg-purple-200 disabled:opacity-40">
                      <Plus size={12}/>
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── STEP 3: Details (Staff now optional) ─────────────────────────────────
function Step3Details({ cartItems, staffList, billDetails, onChange, customerSuggestions = [] }) {
  const services = cartItems.filter(i => i.item_type==='service');
  const [showSuggestions, setShowSuggestions] = useState(null);

  const applyAllStaff = (staffId) => {
    const updated = { ...billDetails };
    services.forEach((_,idx) => { updated[`staff_${idx}`] = staffId; });
    onChange(updated);
  };

  return (
    <div className="space-y-6">
      {services.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest flex-shrink-0">Attended By (Optional)</p>
            {services.length > 1 && (
              <div className="flex-1 max-w-[200px]">
                <StaffPicker staffList={staffList} value={null} onChange={applyAllStaff} placeholder="Apply same to all..."/>
              </div>
            )}
          </div>

          {services.map((svc, idx) => (
            <div key={`svc-${idx}`} className="bg-gray-50 dark:bg-slate-800 rounded-2xl p-4 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-sm font-bold text-gray-800 dark:text-white">{svc.item_name}</p>
                  {svc.item_variant && <span className="text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-300 px-2 py-0.5 rounded-lg mt-0.5 inline-block">{svc.item_variant}</span>}
                </div>
                <span className="text-sm font-black text-purple-600 flex-shrink-0">{fmt(svc.price)}</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <StaffPicker label="Attended by" optional staffList={staffList}
                  value={billDetails[`staff_${idx}`]||null}
                  onChange={id=>onChange({...billDetails,[`staff_${idx}`]:id})}
                  placeholder="Choose staff..."/>
                <StaffPicker label="Helper" optional staffList={staffList}
                  value={billDetails[`helper_${idx}`]||null}
                  excludeId={billDetails[`staff_${idx}`]}
                  onChange={id=>onChange({...billDetails,[`helper_${idx}`]:id})}
                  placeholder="Choose helper..."/>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="space-y-3 bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-800 rounded-2xl p-4">
        <div className="flex items-center gap-2">
          <User size={14} className="text-blue-600"/>
          <p className="text-xs font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Customer Info</p>
          <div className="flex-1 h-px bg-blue-200 dark:bg-blue-800"></div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="relative">
            <User size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
            <input className="w-full bg-white dark:bg-slate-800 border border-blue-200 dark:border-blue-800 rounded-xl pl-8 pr-3 py-2.5 text-sm text-gray-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Customer name" value={billDetails.customer_name||''}
              onFocus={() => setShowSuggestions('name')}
              onBlur={() => setTimeout(() => setShowSuggestions(null), 250)}
              onChange={e=>onChange({...billDetails,customer_name:e.target.value})}/>
            {showSuggestions === 'name' && (billDetails.customer_name || '').trim().length >= 1 && (
              <div className="absolute left-0 right-0 top-full mt-1 bg-white dark:bg-slate-800 border border-blue-200 dark:border-blue-800 rounded-xl shadow-lg z-50 max-h-48 overflow-y-auto">
                {customerSuggestions
                  .filter(c => (c.name || '').toLowerCase().includes((billDetails.customer_name || '').toLowerCase()))
                  .slice(0, 5)
                  .map((c, i) => (
                    <button
                      key={i}
                      type="button"
                      onMouseDown={() => {
                        onChange({
                          ...billDetails,
                          customer_name: c.name,
                          customer_phone: c.phone
                        });
                        setShowSuggestions(null);
                      }}
                      className="w-full text-left px-4 py-2.5 hover:bg-blue-50 dark:hover:bg-slate-700 text-xs text-gray-700 dark:text-gray-200 border-b border-gray-100 dark:border-slate-700 last:border-none flex items-center justify-between"
                    >
                      <span className="font-bold truncate">{c.name}</span>
                      <span className="text-gray-400 dark:text-slate-500 font-mono text-[10px]">{c.phone || 'No phone'}</span>
                    </button>
                  ))}
              </div>
            )}
          </div>
          <div className="relative">
            <Phone size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
            <input type="tel" className="w-full bg-white dark:bg-slate-800 border border-blue-200 dark:border-blue-800 rounded-xl pl-8 pr-3 py-2.5 text-sm text-gray-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Phone" value={billDetails.customer_phone||''}
              onFocus={() => setShowSuggestions('phone')}
              onBlur={() => setTimeout(() => setShowSuggestions(null), 250)}
              onChange={e=>onChange({...billDetails,customer_phone:e.target.value})}/>
            {showSuggestions === 'phone' && (billDetails.customer_phone || '').trim().length >= 1 && (
              <div className="absolute left-0 right-0 top-full mt-1 bg-white dark:bg-slate-800 border border-blue-200 dark:border-blue-800 rounded-xl shadow-lg z-50 max-h-48 overflow-y-auto">
                {customerSuggestions
                  .filter(c => (c.phone || '').includes(billDetails.customer_phone || ''))
                  .slice(0, 5)
                  .map((c, i) => (
                    <button
                      key={i}
                      type="button"
                      onMouseDown={() => {
                        onChange({
                          ...billDetails,
                          customer_name: c.name,
                          customer_phone: c.phone
                        });
                        setShowSuggestions(null);
                      }}
                      className="w-full text-left px-4 py-2.5 hover:bg-blue-50 dark:hover:bg-slate-700 text-xs text-gray-700 dark:text-gray-200 border-b border-gray-100 dark:border-slate-700 last:border-none flex items-center justify-between"
                    >
                      <span className="font-bold truncate">{c.name}</span>
                      <span className="text-gray-400 dark:text-slate-500 font-mono text-[10px]">{c.phone || 'No phone'}</span>
                    </button>
                  ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">Payment Mode</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {PAYMENT_OPTIONS.map(opt=>{
            const active = (billDetails.payment_method||'cash') === opt.id;
            return (
              <button key={opt.id} onClick={()=>onChange({...billDetails,payment_method:opt.id})}
                className={`flex flex-col items-center gap-2 py-3 px-2 rounded-2xl border-2 transition-all ${active?'bg-purple-50 dark:bg-purple-900/20 border-purple-400':'border-gray-200 dark:border-slate-700 hover:border-purple-300'}`}>
                <opt.Icon size={20} className={active?'text-purple-600':'text-gray-400'}/>
                <span className="text-xs font-bold">{opt.label}</span>
              </button>
            );
          })}
        </div>

        {billDetails.payment_method === 'split' && (
          <div className="bg-orange-50 dark:bg-orange-900/10 border border-orange-100 dark:border-orange-900/20 rounded-2xl p-4 space-y-3">
            <p className="text-xs font-semibold text-orange-700 dark:text-orange-400">Enter split amounts</p>
            <div className="grid grid-cols-3 gap-2">
              {['cash','upi','card'].map(m=>(
                <div key={m}>
                  <label className="block text-xs text-gray-500 capitalize mb-1">{m}</label>
                  <div className="relative">
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs">₹</span>
                    <input type="number" min="0"
                      className="w-full pl-6 pr-2 py-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-sm text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-400"
                      placeholder="0" value={billDetails.split?.[m]||''}
                      onChange={e=>onChange({...billDetails,split:{...(billDetails.split||{}),[m]:e.target.value}})}/>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Package Builder Modal ───────────────────────────────────────────────
function PackageBuilderModal({ isOpen, onClose, onSave, packageItem }) {
  const { userToken: token } = useAuth();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');
  const [selectedIds, setSelectedIds] = useState([]);
  const [packageName, setPackageName] = useState('');
  const [packagePrice, setPackagePrice] = useState('');
  const [packageSummary, setPackageSummary] = useState('');

  useEffect(() => {
    if (!isOpen || !token) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await fetch(SALON_API_URL + '/services', { headers: { Authorization: 'Bearer ' + token } });
        const data = await res.json();
        if (!cancelled && data.success) setServices(data.data || []);
      } catch {
        if (!cancelled) toast.error('Could not load services');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [isOpen, token]);

  useEffect(() => {
    if (!isOpen) {
      setSearch('');
      setCategory('All');
      setSelectedIds([]);
      setPackageName('');
      setPackagePrice('');
      setPackageSummary('');
      return;
    }

    setSearch('');
    setCategory('All');
    setSelectedIds((packageItem?.package_services || []).map(svc => svc.id));
    setPackageName(packageItem?.item_name || '');
    setPackagePrice(packageItem?.price !== undefined ? String(packageItem.price) : '');
    setPackageSummary(packageItem?.item_variant || '');
  }, [isOpen, packageItem]);

  if (!isOpen) return null;

  const categories = ['All', ...new Set(services.map(s => s.category).filter(Boolean))];
  const visible = services.filter(s => {
    const term = search.trim().toLowerCase();
    const matchSearch = !term || s.name.toLowerCase().includes(term) || (s.category || '').toLowerCase().includes(term);
    const matchCategory = category === 'All' || s.category === category;
    return matchSearch && matchCategory;
  });
  const selected = services.filter(s => selectedIds.includes(s.id));
  const generatedSummary = selected.length + ' service' + (selected.length === 1 ? '' : 's') + ' selected';

  const toggle = (svc) => {
    setSelectedIds(prev => prev.includes(svc.id) ? prev.filter(id => id !== svc.id) : [...prev, svc.id]);
  };

  const handleSave = () => {
    if (!packageName.trim()) { toast.error('Package name is required'); return; }
    if (selected.length === 0) { toast.error('Select at least one service'); return; }
    const price = parseFloat(packagePrice);
    if (Number.isNaN(price) || price < 0) { toast.error('Enter a valid package price'); return; }

    onSave({
      item_type: 'service',
      item_id: packageItem?.item_id || -Math.floor((Date.now() % 2000000000) + Math.random() * 1000),
      item_name: packageName.trim(),
      item_variant: packageSummary.trim() || generatedSummary,
      price: price,
      qty: packageItem?.qty || 1,
      package_services: selected.map(svc => ({ id: svc.id, name: svc.name, category: svc.category, gender: svc.gender })),
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-5xl max-h-[90vh] overflow-hidden rounded-3xl bg-white dark:bg-slate-900 shadow-2xl border border-gray-200 dark:border-slate-700">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-slate-700">
          <div>
            <h3 className="text-lg font-black text-gray-900 dark:text-white">{packageItem ? 'Edit Package' : 'Make Your Own Package'}</h3>
            <p className="text-xs text-gray-400 mt-0.5">Pick services, adjust the package, and set the final price manually.</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-400">
            <X size={18}/> 
          </button>
        </div>

        <div className="grid lg:grid-cols-[1.4fr_0.9fr] gap-4 p-5 max-h-[calc(90vh-78px)] overflow-y-auto">
          <div className="space-y-4">
            <div className="relative">
              <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-purple-400"/>
              <input
                className="w-full pl-10 pr-4 py-3 bg-purple-50 dark:bg-slate-800 border-2 border-purple-200 dark:border-purple-700 rounded-2xl text-sm text-gray-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 font-medium"
                placeholder="Search services by name or category…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>

            <div className="flex flex-wrap gap-2">
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setCategory(cat)}
                  className={category === cat ? 'px-3 py-1.5 rounded-full text-xs font-bold border transition-all bg-purple-600 text-white border-purple-600' : 'px-3 py-1.5 rounded-full text-xs font-bold border transition-all bg-white dark:bg-slate-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-slate-700 hover:border-purple-300'}
                >
                  {cat}
                </button>
              ))}
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 size={28} className="animate-spin text-purple-600"/>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {visible.map(svc => {
                  const active = selectedIds.includes(svc.id);
                  return (
                    <button
                      key={svc.id}
                      onClick={() => toggle(svc)}
                      className={active ? 'text-left rounded-2xl border-2 p-4 transition-all bg-purple-50 dark:bg-purple-900/20 border-purple-400 shadow-md' : 'text-left rounded-2xl border-2 p-4 transition-all bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 hover:border-purple-300'}
                    >
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-gray-800 dark:text-white truncate">{svc.name}</p>
                          <p className="text-xs text-gray-400 mt-0.5 truncate">{svc.category} · {svc.gender}</p>
                        </div>
                        <div className={active ? 'w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 bg-purple-600 text-white' : 'w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 bg-gray-100 dark:bg-slate-700 text-transparent'}>
                          <Check size={12}/>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-500 dark:text-gray-400">Tap to {active ? 'remove' : 'add'}</span>
                        <span className="font-black text-purple-600">{fmt(svc.price)}</span>
                      </div>
                    </button>
                  );
                })}
                {visible.length === 0 && !loading && (
                  <div className="col-span-full text-center py-16 text-sm text-gray-400 border border-dashed border-gray-200 dark:border-slate-700 rounded-2xl">
                    No services found
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="space-y-4 lg:sticky lg:top-0">
            <div className="rounded-2xl border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/60 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Package size={14} className="text-purple-600"/>
                <p className="text-xs font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Package Details</p>
              </div>
              <input
                value={packageName}
                onChange={e => setPackageName(e.target.value)}
                placeholder="Package name"
                className="w-full px-3 py-2.5 rounded-xl bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <input
                value={packageSummary}
                onChange={e => setPackageSummary(e.target.value)}
                placeholder="Package summary"
                className="w-full px-3 py-2.5 rounded-xl bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <input
                type="number"
                min="0"
                value={packagePrice}
                onChange={e => setPackagePrice(e.target.value)}
                placeholder="Package price"
                className="w-full px-3 py-2.5 rounded-xl bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <div className="rounded-xl bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 p-3">
                <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Selected Services</p>
                {selected.length === 0 ? (
                  <p className="text-sm text-gray-400">Choose services from the list</p>
                ) : (
                  <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                    {selected.map(svc => (
                      <div key={svc.id} className="flex items-center justify-between gap-3 text-sm">
                        <span className="truncate text-gray-700 dark:text-gray-300">{svc.name}</span>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="text-xs text-gray-400">{fmt(svc.price)}</span>
                          <button
                            type="button"
                            onClick={() => toggle(svc)}
                            className="w-6 h-6 rounded-full bg-red-50 dark:bg-red-900/20 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 flex items-center justify-center"
                          >
                            <Minus size={12}/>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-400">Selected services stay editable here, so you can add more or remove any before saving the package.</p>
            </div>

            <div className="flex gap-3">
              <button onClick={onClose} className="flex-1 py-3 rounded-xl border border-gray-200 dark:border-slate-700 text-sm font-bold text-gray-600 dark:text-gray-300">Cancel</button>
              <button onClick={handleSave} className="flex-1 py-3 rounded-xl bg-purple-600 hover:bg-purple-700 text-white text-sm font-black">{packageItem ? 'Update Package' : 'Add Package'}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Right Sidebar: Bill Summary with Payment Selection ───────────────────
function BillSummary({ cartItems, billDetails, staffList, onComplete, onHold, saving, onPaymentChange, onRemoveItem, onDiscountChange, onResizeStart, onOpenPackageBuilder, onEditPackage, onUpdateItem, onUpdateItemQty, customerSuggestions = [] }) {
  const services = cartItems.filter(i=>i.item_type==='service');
  const products = cartItems.filter(i=>i.item_type==='product');
  const subtotal = cartItems.reduce((s,i)=>s+parseFloat(i.price)*(i.qty||1),0);
  const [showSuggestions, setShowSuggestions] = useState(null); // 'name' | 'phone' | null
  
  // Discount calculation
  const discountType = billDetails.discount_type || 'amount'; // 'amount' or 'percent'
  const discountValue = parseFloat(billDetails.discount_value) || 0;
  const discountAmount = discountType === 'percent' 
    ? (subtotal * discountValue / 100)
    : discountValue;
  
  const total = Math.max(0, subtotal - discountAmount);
  const getStaff = (id) => staffList.find(s=>s.id===id);
  const isCustomPackage = (item) => item.item_type === 'service' && Number(item.item_id) < 0;

  return (
    <div className="h-full flex flex-col bg-white dark:bg-slate-900 border-l border-gray-200 dark:border-slate-800">
      <div
        onMouseDown={onResizeStart}
        title="Drag to resize summary"
        className="absolute -left-1 top-0 bottom-0 w-2 cursor-col-resize z-10 hidden lg:block"
      >
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-1 h-16 rounded-full bg-gray-300 dark:bg-slate-600"></div>
      </div>

      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-slate-800 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20">
        <h3 className="font-black text-gray-900 dark:text-white flex items-center gap-2">
          <ReceiptText size={20} className="text-purple-600"/> Bill Summary
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          {cartItems.length} item{cartItems.length !== 1 ? 's' : ''} added
        </p>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {cartItems.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-slate-800 flex items-center justify-center">
              <ReceiptText size={32} className="text-gray-300 dark:text-slate-600"/>
            </div>
            <p className="text-sm font-semibold text-gray-400">No items added yet</p>
            <p className="text-xs text-gray-300 dark:text-slate-600 mt-1">Add services or products to start</p>
          </div>
        ) : (
          <>
            <div className="space-y-3 bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-800 rounded-2xl p-3">
              <div className="flex items-center gap-2">
                <User size={14} className="text-blue-600"/>
                <p className="text-xs font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Customer Info</p>
                <div className="flex-1 h-px bg-blue-200 dark:bg-blue-800"></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div className="relative">
                  <User size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
                  <input
                    className="w-full bg-white dark:bg-slate-800 border border-blue-200 dark:border-blue-800 rounded-xl pl-8 pr-3 py-2.5 text-sm text-gray-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Customer name"
                    value={billDetails.customer_name || ''}
                    onFocus={() => setShowSuggestions('name')}
                    onBlur={() => setTimeout(() => setShowSuggestions(null), 250)}
                    onChange={e => onDiscountChange({ ...billDetails, customer_name: e.target.value })}
                  />
                  {showSuggestions === 'name' && (billDetails.customer_name || '').trim().length >= 1 && (
                    <div className="absolute left-0 right-0 top-full mt-1 bg-white dark:bg-slate-800 border border-blue-200 dark:border-blue-800 rounded-xl shadow-lg z-50 max-h-48 overflow-y-auto">
                      {customerSuggestions
                        .filter(c => (c.name || '').toLowerCase().includes((billDetails.customer_name || '').toLowerCase()))
                        .slice(0, 5)
                        .map((c, i) => (
                          <button
                            key={i}
                            type="button"
                            onMouseDown={() => {
                              onDiscountChange({
                                ...billDetails,
                                customer_name: c.name,
                                customer_phone: c.phone
                              });
                              setShowSuggestions(null);
                            }}
                            className="w-full text-left px-4 py-2.5 hover:bg-blue-50 dark:hover:bg-slate-700 text-xs text-gray-700 dark:text-gray-200 border-b border-gray-100 dark:border-slate-700 last:border-none flex items-center justify-between"
                          >
                            <span className="font-bold truncate">{c.name}</span>
                            <span className="text-gray-450 dark:text-slate-500 font-mono text-[10px]">{c.phone || 'No phone'}</span>
                          </button>
                        ))}
                    </div>
                  )}
                </div>
                <div className="relative">
                  <Phone size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
                  <input
                    type="tel"
                    className="w-full bg-white dark:bg-slate-800 border border-blue-200 dark:border-blue-800 rounded-xl pl-8 pr-3 py-2.5 text-sm text-gray-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Phone"
                    value={billDetails.customer_phone || ''}
                    onFocus={() => setShowSuggestions('phone')}
                    onBlur={() => setTimeout(() => setShowSuggestions(null), 250)}
                    onChange={e => onDiscountChange({ ...billDetails, customer_phone: e.target.value })}
                  />
                  {showSuggestions === 'phone' && (billDetails.customer_phone || '').trim().length >= 1 && (
                    <div className="absolute left-0 right-0 top-full mt-1 bg-white dark:bg-slate-800 border border-blue-200 dark:border-blue-800 rounded-xl shadow-lg z-50 max-h-48 overflow-y-auto">
                      {customerSuggestions
                        .filter(c => (c.phone || '').includes(billDetails.customer_phone || ''))
                        .slice(0, 5)
                        .map((c, i) => (
                          <button
                            key={i}
                            type="button"
                            onMouseDown={() => {
                              onDiscountChange({
                                ...billDetails,
                                customer_name: c.name,
                                customer_phone: c.phone
                              });
                              setShowSuggestions(null);
                            }}
                            className="w-full text-left px-4 py-2.5 hover:bg-blue-50 dark:hover:bg-slate-700 text-xs text-gray-700 dark:text-gray-200 border-b border-gray-100 dark:border-slate-700 last:border-none flex items-center justify-between"
                          >
                            <span className="font-bold truncate">{c.name}</span>
                            <span className="text-gray-450 dark:text-slate-500 font-mono text-[10px]">{c.phone || 'No phone'}</span>
                          </button>
                        ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Services */}
            {services.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Scissors size={14} className="text-purple-600"/>
                  <p className="text-xs font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Services</p>
                  <div className="flex-1 h-px bg-gray-200 dark:bg-slate-700"></div>
                </div>
                {services.map((svc,idx)=>{
                  const staff = getStaff(billDetails[`staff_${idx}`]);
                  const helper = getStaff(billDetails[`helper_${idx}`]);
                  return (
                    <div key={idx} onClick={isCustomPackage(svc) ? () => onEditPackage(svc) : undefined} className={`group bg-white dark:bg-slate-800 rounded-xl p-3 border border-gray-200 dark:border-slate-700 hover:border-purple-300 dark:hover:border-purple-600 transition-all hover:shadow-md ${isCustomPackage(svc) ? 'cursor-pointer' : ''}`}>
                      <div className="flex items-start gap-3">
                        <div className="flex-1 min-w-0">
                          {isCustomPackage(svc) ? (
                            <div className="space-y-2">
                              <div className="flex items-center justify-between gap-2">
                                <span className="inline-flex items-center rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 px-2 py-0.5 text-[11px] font-bold">Custom Package</span>
                                <button type="button" onClick={(e) => { e.stopPropagation(); onEditPackage(svc); }} className="text-xs font-bold text-purple-600 hover:text-purple-700">Edit services</button>
                              </div>
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-xs text-gray-400">Package price</span>
                                <input
                                  type="number"
                                  min="0"
                                  value={svc.price}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={(e) => onUpdateItem(svc.item_id, svc.item_variant, 'service', { price: e.target.value })}
                                  className="w-28 px-2.5 py-1.5 rounded-lg border border-purple-200 dark:border-purple-700 bg-white dark:bg-slate-900 text-sm font-black text-right text-purple-600 focus:outline-none focus:ring-2 focus:ring-purple-500"
                                />
                              </div>
                              <input
                                type="text"
                                value={svc.item_name || ''}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => onUpdateItem(svc.item_id, svc.item_variant, 'service', { item_name: e.target.value })}
                                placeholder="Package name"
                                className="w-full px-3 py-2 rounded-lg border border-purple-200 dark:border-purple-700 bg-white dark:bg-slate-900 text-sm font-bold text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                              />
                              <input
                                type="text"
                                value={svc.item_variant || ''}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => onUpdateItem(svc.item_id, svc.item_variant, 'service', { item_variant: e.target.value })}
                                placeholder="Package summary"
                                className="w-full px-3 py-2 rounded-lg border border-purple-100 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20 text-xs font-medium text-purple-700 dark:text-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                              />
                            </div>
                          ) : (
                            <>
                              <div className="flex items-start justify-between gap-2 mb-1">
                                <p className="text-sm font-bold text-gray-800 dark:text-white">{svc.item_name}</p>
                                <span className="text-sm font-black text-purple-600 flex-shrink-0">{fmt(svc.price)}</span>
                              </div>
                              {svc.item_variant && (
                                <span className="inline-block text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-300 px-2 py-0.5 rounded-md mb-1">
                                  {svc.item_variant}
                                </span>
                              )}
                            </>
                          )}
                          <div className="flex items-center justify-between gap-3 mt-2">
                            <div className="flex items-center gap-2">
                              <button type="button" onClick={(e) => { e.stopPropagation(); onUpdateItemQty(svc.item_id, svc.item_variant, 'service', 'decrease'); }} className="w-7 h-7 rounded-full border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-gray-700 dark:text-gray-300 flex items-center justify-center">
                                <Minus size={12}/>
                              </button>
                              <span className="min-w-[24px] text-center text-sm font-bold text-gray-700 dark:text-gray-200">{svc.qty || 1}</span>
                              <button type="button" onClick={(e) => { e.stopPropagation(); onUpdateItemQty(svc.item_id, svc.item_variant, 'service', 'increase'); }} className="w-7 h-7 rounded-full border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-gray-700 dark:text-gray-300 flex items-center justify-center">
                                <Plus size={12}/>
                              </button>
                            </div>
                            <span className="text-xs font-bold text-purple-600">{fmt((parseFloat(svc.price) || 0) * (svc.qty || 1))}</span>
                          </div>
                          {staff && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1 mt-1">
                              <User size={10}/>{staff.name}
                            </p>
                          )}
                          {helper && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                              <Users size={10}/>Helper: {helper.name}
                            </p>
                          )}
                        </div>
                        <button
                          onClick={() => onRemoveItem(svc.item_id, svc.item_variant, 'service')}
                          className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500 transition-all"
                          title="Remove service"
                        >
                          <X size={14}/>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Products */}
            {products.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Package size={14} className="text-blue-600"/>
                  <p className="text-xs font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Products</p>
                  <div className="flex-1 h-px bg-gray-200 dark:bg-slate-700"></div>
                </div>
                {products.map((prod,idx)=>(
                  <div key={idx} className="group bg-white dark:bg-slate-800 rounded-xl p-3 border border-gray-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-600 transition-all hover:shadow-md">
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <p className="text-sm font-bold text-gray-800 dark:text-white truncate">{prod.item_name}</p>
                          <span className="text-sm font-black text-blue-600 flex-shrink-0">{fmt((parseFloat(prod.price) || 0) * (prod.qty || 1))}</span>
                        </div>
                        <div className="flex items-center justify-between gap-3 mt-2">
                          <div className="flex items-center gap-2">
                            <button type="button" onClick={(e) => { e.stopPropagation(); (prod.qty || 1) <= 1 ? onRemoveItem(prod.item_id, null, 'product') : onUpdateItemQty(prod.item_id, null, 'product', 'decrease'); }} className="w-7 h-7 rounded-full border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-gray-700 dark:text-gray-300 flex items-center justify-center">
                              <Minus size={12}/>
                            </button>
                            <span className="min-w-[24px] text-center text-sm font-bold text-gray-700 dark:text-gray-200">{prod.qty || 1}</span>
                            <button type="button" onClick={(e) => { e.stopPropagation(); onUpdateItemQty(prod.item_id, null, 'product', 'increase'); }} className="w-7 h-7 rounded-full border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-gray-700 dark:text-gray-300 flex items-center justify-center">
                              <Plus size={12}/>
                            </button>
                          </div>
                          <span className="text-xs text-gray-500 dark:text-gray-400">{fmt(prod.price)} each</span>
                        </div>
                      </div>
                      <button
                        onClick={() => onRemoveItem(prod.item_id, null, 'product')}
                        className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500 transition-all"
                        title="Remove product"
                      >
                        <X size={14}/>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Discount Section */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl p-3 border border-green-200 dark:border-green-800">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-bold text-green-700 dark:text-green-400 uppercase tracking-wide">Discount</p>
                <div className="flex gap-1 bg-white dark:bg-slate-800 rounded-lg p-0.5 border border-green-200 dark:border-green-700">
                  <button
                    onClick={() => onDiscountChange({ ...billDetails, discount_type: 'amount' })}
                    className={`px-2 py-1 rounded text-xs font-semibold transition-all ${
                      discountType === 'amount'
                        ? 'bg-green-600 text-white'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700'
                    }`}
                  >
                    ₹
                  </button>
                  <button
                    onClick={() => onDiscountChange({ ...billDetails, discount_type: 'percent' })}
                    className={`px-2 py-1 rounded text-xs font-semibold transition-all ${
                      discountType === 'percent'
                        ? 'bg-green-600 text-white'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700'
                    }`}
                  >
                    %
                  </button>
                </div>
              </div>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  max={discountType === 'percent' ? 100 : subtotal}
                  step={discountType === 'percent' ? 1 : 10}
                  value={discountValue || ''}
                  onChange={(e) => onDiscountChange({ ...billDetails, discount_value: e.target.value })}
                  className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-green-300 dark:border-green-700 rounded-lg text-sm font-semibold text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder={discountType === 'percent' ? '0%' : '₹0'}
                />
                {discountAmount > 0 && (
                  <p className="text-xs text-green-600 dark:text-green-400 mt-1 font-semibold">
                    Discount: -{fmt(discountAmount)}
                    {discountType === 'percent' && ` (${discountValue}%)`}
                  </p>
                )}
              </div>
            </div>

            {/* Totals */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl overflow-hidden border border-purple-200 dark:border-purple-800">
              <div className="flex justify-between px-4 py-2.5 text-sm">
                <span className="text-gray-600 dark:text-gray-400 font-medium">Subtotal</span>
                <span className="font-bold text-gray-800 dark:text-white">{fmt(subtotal)}</span>
              </div>
              {discountAmount > 0 && (
                <div className="flex justify-between px-4 py-2.5 border-t border-purple-100 dark:border-purple-800 text-sm">
                  <span className="text-green-600 dark:text-green-400 font-medium">Discount</span>
                  <span className="font-bold text-green-600 dark:text-green-400">-{fmt(discountAmount)}</span>
                </div>
              )}
              <div className="flex justify-between px-4 py-3 border-t-2 border-purple-200 dark:border-purple-700 bg-white/50 dark:bg-slate-800/50">
                <span className="font-black text-gray-900 dark:text-white">Total</span>
                <span className="font-black text-purple-600 dark:text-purple-400 text-lg">{fmt(total)}</span>
              </div>
            </div>

            {/* Payment Method */}
            <div>
              <p className="text-xs font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-2">Payment Method</p>
              <div className="grid grid-cols-3 gap-2">
                {PAYMENT_OPTIONS.filter(opt => opt.id !== 'split').map(opt => {
                  const active = (billDetails.payment_method || 'cash') === opt.id;
                  return (
                    <button
                      key={opt.id}
                      onClick={() => onPaymentChange(opt.id)}
                      className={`flex flex-col items-center gap-2 py-3 px-2 rounded-xl border-2 transition-all ${
                        active
                          ? 'bg-purple-600 border-purple-600 shadow-lg scale-105'
                          : 'bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 hover:border-purple-300'
                      }`}
                    >
                      <opt.Icon size={18} className={active ? 'text-white' : 'text-gray-400'} />
                      <span className={`text-xs font-bold ${active ? 'text-white' : 'text-gray-600 dark:text-gray-400'}`}>
                        {opt.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Fixed bottom buttons */}
      {cartItems.length > 0 && (
        <div className="p-4 border-t-2 border-gray-200 dark:border-slate-800 space-y-2 bg-white dark:bg-slate-900 shadow-lg">
          <button
            onClick={onComplete}
            disabled={saving}
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl text-sm font-black bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white shadow-lg hover:shadow-xl transition-all disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saving ? <Loader2 size={18} className="animate-spin"/> : <CheckCircle size={18}/>}
            {saving ? 'Processing...' : 'Complete Bill'}
          </button>
          <button
            onClick={onHold}
            disabled={saving}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-200 border-2 border-gray-200 dark:border-slate-700 hover:border-purple-300 transition-all disabled:opacity-60"
          >
            <Clock size={16}/> Save & Hold
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Station Management Modal ──────────────────────────────────────────────
function StationModal({ isOpen, onClose, stations, onSave, onDelete }) {
  const [name, setName] = useState('');
  const [type, setType] = useState('chair');
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const handleSubmit = async () => {
    if (!name.trim()) { toast.error('Station name required'); return; }
    setSaving(true);
    try {
      await onSave({ id: editId, name: name.trim(), station_type: type });
      setName(''); setType('chair'); setEditId(null);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (station) => {
    setEditId(station.id);
    setName(station.name);
    setType(station.station_type);
  };

  const handleDelete = async (id) => {
    await onDelete(id);
  };

  const clearEdit = () => {
    setEditId(null);
    setName('');
    setType('chair');
  };

  if (!isOpen) return null;

  const filteredStations = stations.filter(station =>
    (station.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (STATION_LABELS[station.station_type] || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white dark:bg-slate-900 rounded-[2rem] shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden border border-gray-100 dark:border-slate-800">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 dark:border-slate-800 bg-gray-50/50 dark:bg-slate-900/50">
          <div>
            <h3 className="font-black text-gray-900 dark:text-white text-lg flex items-center gap-2">
              <Settings size={20} className="text-purple-600"/> Manage Workspaces
            </h3>
            <p className="text-xs text-gray-400 mt-0.5">Add, edit, or configure your salon's service workspaces</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-150 dark:hover:bg-slate-800 text-gray-400 hover:text-gray-650 transition-colors">
            <X size={18}/>
          </button>
        </div>

        {/* Content Body */}
        <div className="flex-1 overflow-hidden grid grid-cols-1 md:grid-cols-5 divide-y md:divide-y-0 md:divide-x divide-gray-100 dark:divide-slate-800">
          
          {/* Left Column: Creator/Editor form */}
          <div className="md:col-span-2 p-6 space-y-5 flex flex-col justify-start overflow-y-auto">
            <div className="space-y-1">
              <h4 className="text-xs font-bold text-gray-450 uppercase tracking-widest">
                {editId ? 'Modify Workspace' : 'Create New Workspace'}
              </h4>
              <p className="text-xs text-gray-400">Set a unique name and select its main service type.</p>
            </div>

            <div className="space-y-4">
              <div className="relative">
                <input
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-800 border-2 border-gray-250 dark:border-slate-700 rounded-2xl text-sm text-gray-800 dark:text-white placeholder-gray-400 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 font-medium transition-all"
                  placeholder="Workspace name (e.g., Chair 1, Shampoo A)"
                  value={name}
                  onChange={e=>setName(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <label className="text-xs font-bold text-gray-500 dark:text-gray-400">Workspace Type</label>
                <div className="grid grid-cols-2 gap-2.5">
                  {['chair','shampoo','manipedi','room'].map(t=>{
                    const Icon = STATION_ICONS[t];
                    const active = type === t;
                    let activeTheme = 'border-purple-400 bg-purple-50/50 dark:bg-purple-900/10 text-purple-600';
                    if (t === 'shampoo' && active) activeTheme = 'border-blue-400 bg-blue-50/50 dark:bg-blue-900/10 text-blue-600';
                    if (t === 'manipedi' && active) activeTheme = 'border-pink-400 bg-pink-50/50 dark:bg-pink-900/10 text-pink-600';
                    if (t === 'room' && active) activeTheme = 'border-violet-400 bg-violet-50/50 dark:bg-violet-900/10 text-violet-600';

                    return (
                      <button key={t} type="button" onClick={()=>setType(t)}
                        className={`flex items-center gap-3 p-3 rounded-2xl border-2 transition-all text-left ${active ? activeTheme : 'border-gray-100 dark:border-slate-800 bg-white dark:bg-slate-800 hover:border-gray-200 dark:hover:border-slate-700'}`}>
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${active ? 'bg-white dark:bg-slate-900 shadow-md' : 'bg-gray-50 dark:bg-slate-900'}`}>
                          <Icon size={18} className={active ? '' : 'text-gray-400'}/>
                        </div>
                        <div className="min-w-0">
                          <span className="text-xs font-bold block truncate text-gray-800 dark:text-white">{STATION_LABELS[t] || t}</span>
                          <span className="text-[10px] text-gray-400 block capitalize">{t}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-2 pt-2">
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={saving}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl bg-purple-600 hover:bg-purple-700 text-white font-black text-sm shadow-lg shadow-purple-100 dark:shadow-purple-900/20 transition-all disabled:opacity-60 active:scale-[0.98]"
                >
                  {saving ? <Loader2 size={16} className="animate-spin"/> : editId ? <Save size={16}/> : <Plus size={16}/>}
                  {editId ? 'Save Modifications' : 'Create Workspace'}
                </button>
                {editId && (
                  <button
                    type="button"
                    onClick={clearEdit}
                    className="w-full py-2.5 rounded-2xl border border-gray-200 dark:border-slate-700 text-xs font-semibold text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
                  >
                    Cancel Editing
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Right Column: Listing */}
          <div className="md:col-span-3 p-6 flex flex-col min-h-0 bg-gray-50/30 dark:bg-slate-900/20">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4 flex-shrink-0">
              <div>
                <h4 className="text-xs font-bold text-gray-450 uppercase tracking-widest">
                  Your Workspaces ({stations.length})
                </h4>
                <p className="text-xs text-gray-400 mt-0.5">Click edit or delete to manage existing setups</p>
              </div>
              <div className="relative w-full sm:w-48">
                <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"/>
                <input
                  type="text"
                  placeholder="Filter workspaces…"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="w-full pl-7 pr-3 py-1.5 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-xs text-gray-700 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-purple-500"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto pr-1 space-y-2.5">
              {filteredStations.length === 0 ? (
                <div className="text-center py-16 border-2 border-dashed border-gray-205 dark:border-slate-800 rounded-[1.5rem]">
                  <Settings size={28} className="mx-auto text-gray-300 dark:text-slate-700 mb-2"/>
                  <p className="text-sm text-gray-400">
                    {searchTerm ? 'No matching workspaces found' : 'No workspaces created yet'}
                  </p>
                  <p className="text-xs text-gray-300 dark:text-slate-600 mt-1">
                    {searchTerm ? 'Try adjusting your search criteria' : 'Use the left form to add your first workspace'}
                  </p>
                </div>
              ) : (
                filteredStations.map(station => {
                  const Icon = STATION_ICONS[station.station_type] || Armchair;
                  
                  // Color highlight per type
                  let borderHighlight = 'border-l-purple-500';
                  let badgeColor = 'bg-purple-100/70 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300';
                  if (station.station_type === 'shampoo') {
                    borderHighlight = 'border-l-blue-500';
                    badgeColor = 'bg-blue-100/70 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
                  } else if (station.station_type === 'manipedi') {
                    borderHighlight = 'border-l-pink-500';
                    badgeColor = 'bg-pink-100/70 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300';
                  } else if (station.station_type === 'room') {
                    borderHighlight = 'border-l-violet-500';
                    badgeColor = 'bg-violet-100/70 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300';
                  }

                  const activeEdit = editId === station.id;

                  return (
                    <div key={station.id}
                      className={`flex items-center gap-3 p-3.5 bg-white dark:bg-slate-800 rounded-2xl border border-gray-150 dark:border-slate-800 hover:border-gray-200 dark:hover:border-slate-700 transition-all hover:-translate-y-[1px] hover:shadow-sm border-l-4 ${borderHighlight} ${activeEdit ? 'ring-2 ring-purple-500 border-purple-500' : ''}`}>
                      
                      <div className={`w-9 h-9 rounded-xl bg-gray-50 dark:bg-slate-900 flex items-center justify-center flex-shrink-0`}>
                        <Icon size={16} className="text-gray-500 dark:text-gray-400"/>
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800 dark:text-white truncate">{station.name}</p>
                        <span className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded-full mt-1 ${badgeColor}`}>
                          {STATION_LABELS[station.station_type] || station.station_type}
                        </span>
                      </div>

                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => handleEdit(station)}
                          title="Edit workspace"
                          className={`p-2 rounded-xl transition-all ${activeEdit ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' : 'hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-500 dark:text-gray-450'}`}
                        >
                          <Edit2 size={13}/>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(station.id)}
                          title="Delete workspace"
                          className="p-2 rounded-xl hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500 dark:text-red-400 transition-colors"
                        >
                          <Trash2 size={13}/>
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

// ─── Main Component: Multi-Bill Management ────────────────────────────────
export default function SalonBilling() {
  const { userToken: token } = useAuth();
  const navigate = useNavigate();

  // View state
  const [view, setView] = useState('list'); // 'list' | 'create' | 'edit'
  const [currentBillId, setCurrentBillId] = useState(null);

  // Data
  const [pendingBills, setPendingBills] = useState([]);
  const [stations, setStations] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customerSuggestions, setCustomerSuggestions] = useState([]);

  useEffect(() => {
    const fetchCustomers = async () => {
      if (!token) return;
      try {
        const [billsRes, leadsRes] = await Promise.all([
          fetch(`${SALON_API_URL}/bills?limit=150`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${SALON_API_URL}/leads`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null)
        ]);
        
        let billsData = { success: false, data: [] };
        if (billsRes && billsRes.ok) {
          billsData = await billsRes.ok ? await billsRes.json() : { success: false, data: [] };
        }
        
        let leadsData = { success: false, data: [] };
        if (leadsRes && leadsRes.ok) {
          leadsData = await leadsRes.ok ? await leadsRes.json() : { success: false, data: [] };
        }

        const uniqueCustomers = [];
        const seen = new Set();

        const addCustomer = (name, phone) => {
          const cleanName = (name || '').trim();
          const cleanPhone = (phone || '').trim();
          if (!cleanName && !cleanPhone) return;
          const key = `${cleanName.toLowerCase()}|||${cleanPhone}`;
          if (!seen.has(key)) {
            seen.add(key);
            uniqueCustomers.push({ name: cleanName, phone: cleanPhone });
          }
        };

        if (billsData.success && Array.isArray(billsData.data)) {
          billsData.data.forEach(bill => {
            addCustomer(bill.customer_name, bill.customer_phone);
          });
        }

        if (leadsData.success && Array.isArray(leadsData.data)) {
          leadsData.data.forEach(lead => {
            addCustomer(lead.name, lead.phone);
          });
        }

        setCustomerSuggestions(uniqueCustomers);
      } catch (err) {
        console.error('Failed to load customer suggestions', err);
      }
    };
    fetchCustomers();
  }, [token]);

  // Current bill state
  const [step, setStep] = useState(1);
  const [cartItems, setCartItems] = useState([]);
  const [billDetails, setBillDetails] = useState({ payment_method: 'cash' });
  const [selectedStation, setSelectedStation] = useState(null);
  const [saving, setSaving] = useState(false);
  const [packageBuilderOpen, setPackageBuilderOpen] = useState(false);
  const [editingPackageItem, setEditingPackageItem] = useState(null);
  const [summaryWidth, setSummaryWidth] = useState(() => {
    const saved = Number(localStorage.getItem('salonSummaryWidth'));
    return Number.isFinite(saved) && saved >= 360 && saved <= 560 ? saved : 420;
  });

  // Modals
  const [stationModalOpen, setStationModalOpen] = useState(false);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const startSummaryResize = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = summaryWidth;
    const onMove = (moveEvent) => {
      const next = Math.min(560, Math.max(360, startWidth + (startX - moveEvent.clientX)));
      setSummaryWidth(next);
      localStorage.setItem('salonSummaryWidth', String(next));
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const handleAddPackage = (pkgItem) => {
    setCartItems(prev => {
      const exists = prev.some(i => i.item_id === pkgItem.item_id && i.item_type === pkgItem.item_type);
      if (exists) {
        return prev.map(i => (i.item_id === pkgItem.item_id && i.item_type === pkgItem.item_type ? { ...i, ...pkgItem } : i));
      }
      return [...prev, pkgItem];
    });
    setPackageBuilderOpen(false);
    setEditingPackageItem(null);
    toast.success(editingPackageItem ? 'Package updated' : 'Package added to bill');
  }; 

  const openPackageBuilder = (pkgItem = null) => {
    setEditingPackageItem(pkgItem);
    setPackageBuilderOpen(true);
  };

  // ── Load data ──────────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [pendingRes, stationsRes, staffRes] = await Promise.all([
        fetch(`${SALON_API_URL}/pending-bills`, { headers }),
        fetch(`${SALON_API_URL}/stations`, { headers }),
        fetch(`${SALON_API_URL}/staff`, { headers }),
      ]);
      const [pendingData, stationsData, staffData] = await Promise.all([
        pendingRes.json(),
        stationsRes.json(),
        staffRes.json(),
      ]);
      if (pendingData.success) setPendingBills(pendingData.data);
      if (stationsData.success) setStations(stationsData.data);
      if (staffData.success) setStaffList(staffData.data);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [token]); // eslint-disable-line

  useEffect(() => { loadData(); }, [loadData]);

  // ── Cart helpers ───────────────────────────────────────────────────────
  const addItem = useCallback((item) => {
    setCartItems(prev => {
      const exists = prev.find(i=>i.item_id===item.item_id && i.item_type===item.item_type && i.item_variant===item.item_variant);
      return exists ? prev : [...prev, item];
    });
  }, []);

  const removeItem = useCallback((itemId, variant, type='service') => {
    setCartItems(prev => prev.filter(i=>!(i.item_id===itemId && i.item_type===type && i.item_variant===variant)));
  }, []);

  const updateProductQty = useCallback((itemId, qty) => {
    setCartItems(prev => prev.map(i=>i.item_id===itemId && i.item_type==='product' ? {...i,qty} : i));
  }, []);

  const updateCartItem = useCallback((itemId, variant, type = 'service', patch = {}) => {
    setCartItems(prev => prev.map(i => {
      if (i.item_id !== itemId || i.item_type !== type || i.item_variant !== variant) return i;
      return { ...i, ...patch };
    }));
  }, []);

  const updateItemQty = useCallback((itemId, variant, type = 'service', direction = 'increase') => {
    setCartItems(prev => prev.map(i => {
      if (i.item_id !== itemId || i.item_type !== type || i.item_variant !== variant) return i;
      const currentQty = parseInt(i.qty, 10) || 1;
      const nextQty = direction === 'decrease' ? Math.max(1, currentQty - 1) : currentQty + 1;
      return { ...i, qty: nextQty };
    }));
  }, []);

  // ── Station management ─────────────────────────────────────────────────
  const handleSaveStation = async ({ id, name, station_type }) => {
    try {
      const url = id ? `${SALON_API_URL}/stations/${id}` : `${SALON_API_URL}/stations`;
      const method = id ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers, body: JSON.stringify({ name, station_type }) });
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      toast.success(data.message);
      await loadData();
    } catch (err) {
      toast.error(err.message || 'Failed to save station');
    }
  };

  const handleDeleteStation = async (id) => {
    try {
      const res = await fetch(`${SALON_API_URL}/stations/${id}`, { method: 'DELETE', headers });
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      toast.success(data.message);
      await loadData();
    } catch (err) {
      toast.error(err.message || 'Failed to delete station');
    }
  };

  // ── Bill actions ───────────────────────────────────────────────────────
  const handleNewBill = (stationId = null) => {
    setView('create');
    setCurrentBillId(null);
    setStep(1);
    setCartItems([]);
    setBillDetails({ payment_method: 'cash' });
    setSelectedStation(stationId);
  };

  const handleEditBill = (bill) => {
    setView('edit');
    setCurrentBillId(bill.id);
    setStep(1);
    setSelectedStation(bill.station_id);
    
    // Reconstruct cart items
    const items = bill.items.map(item => ({
      item_type: item.type,
      item_id: item.item_id,
      item_name: item.name,
      item_variant: item.variant,
      price: item.price,
      qty: item.qty || 1,
      package_services: item.package_services || [],
    }));
    setCartItems(items);

    // Reconstruct bill details
    const savedDiscount = parseFloat(bill.discount) || 0;
    const details = {
      customer_name: bill.customer_name,
      customer_phone: bill.customer_phone,
      payment_method: bill.payment_method,
      split: typeof bill.payment_split === 'string' ? JSON.parse(bill.payment_split) : (bill.payment_split || null),
      discount: bill.discount,
      discount_type: 'amount',
      discount_value: savedDiscount ? String(savedDiscount) : '',
    };

    // Add staff assignments
    bill.items.forEach((item, idx) => {
      if (item.type === 'service') {
        if (item.staff_id) details[`staff_${idx}`] = item.staff_id;
        if (item.helper_staff_id) details[`helper_${idx}`] = item.helper_staff_id;
      }
    });

    setBillDetails(details);
  };

  const safeJsonStringify = (data) => {
    const seen = new WeakSet();
    return JSON.stringify(data, (key, value) => {
      if (typeof value === 'object' && value !== null) {
        if (seen.has(value) || (typeof window !== 'undefined' && value instanceof Window) || (typeof Element !== 'undefined' && value instanceof Element) || (value.nativeEvent && value.target)) {
          return undefined;
        }
        seen.add(value);
      }
      return value;
    });
  };

  const savePendingBill = async () => {
    if (cartItems.length === 0) throw new Error("Add at least one item");

    const items = cartItems.map(item => {
      const isService = item.item_type === "service";
      const svcIdx = isService ? cartItems.filter(i=>i.item_type === "service").indexOf(item) : null;
      const rawStaffId = isService ? billDetails["staff_" + svcIdx] : null;
      const rawHelperId = isService ? billDetails["helper_" + svcIdx] : null;
      return {
        item_type: item.item_type,
        item_id: item.item_id,
        item_name: item.item_name,
        item_variant: item.item_variant||null,
        price: parseFloat(item.price) || 0,
        qty: parseInt(item.qty, 10) || 1,
        package_services: Array.isArray(item.package_services)
          ? item.package_services.map(ps => ({ id: ps.id, name: ps.name, category: ps.category, gender: ps.gender }))
          : null,
        staff_id: typeof rawStaffId !== 'object' ? (rawStaffId || null) : null,
        helper_staff_id: typeof rawHelperId !== 'object' ? (rawHelperId || null) : null,
      };
    });

    const subtotal = cartItems.reduce((s,i)=>s+parseFloat(i.price)*(i.qty||1),0);
    const discountType = billDetails.discount_type || "amount";
    const discountValue = parseFloat(billDetails.discount_value) || 0;
    const discountAmount = discountType === "percent" 
      ? (subtotal * discountValue / 100)
      : discountValue;

    const payload = {
      station_id: typeof selectedStation !== 'object' ? selectedStation : null,
      items,
      customer_name: typeof billDetails.customer_name === 'string' ? billDetails.customer_name : null,
      customer_phone: typeof billDetails.customer_phone === 'string' ? billDetails.customer_phone : null,
      payment_method: typeof billDetails.payment_method === 'string' ? billDetails.payment_method : "cash",
      payment_split: billDetails.payment_method === "split" && typeof billDetails.split === 'object' ? billDetails.split : null,
      discount: discountAmount,
    };

    const url = currentBillId 
      ? SALON_API_URL + "/pending-bills/" + currentBillId
      : SALON_API_URL + "/pending-bills";
    const method = currentBillId ? "PUT" : "POST";
    const res = await fetch(url, { method, headers, body: safeJsonStringify(payload) });
    const data = await res.json();
    if (!data.success) throw new Error(data.message);
    return data;
  };

  const handleHoldBill = async () => {
    setSaving(true);
    try {
      const data = await savePendingBill();
      toast.success(data.message);
      await loadData();
      setView("list");
    } catch (err) {
      toast.error(err.message || "Failed to save bill");
    } finally {
      setSaving(false);
    }
  };

  const handleCompleteBill = async () => {
    if (cartItems.length === 0) { toast.error("Add at least one item"); return; }

    setSaving(true);
    try {
      const saved = await savePendingBill();
      const billId = currentBillId || saved.data?.id;
      if (!billId) throw new Error("Could not find saved bill to complete");

      const res = await fetch(SALON_API_URL + "/pending-bills/" + billId + "/complete", {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ createdDate: undefined })
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      toast.success(data.message);
      setPendingBills(prev => prev.filter(b => b.id !== billId));
      setCurrentBillId(null);
      setCartItems([]);
      setBillDetails({ payment_method: "cash" });
      await loadData();
      setView("list");
      navigate('/salon/dashboard');
    } catch (err) {
      toast.error(err.message || "Failed to complete bill");
    } finally {
      setSaving(false);
    }
  };

  const handleCompletePendingBill = async (billId) => {
    setSaving(true);
    try {
      const res = await fetch(`${SALON_API_URL}/pending-bills/${billId}/complete`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ createdDate: undefined })
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      toast.success(data.message);
      setPendingBills(prev => prev.filter(b => b.id !== billId));
      await loadData();
      navigate('/salon/dashboard');
    } catch (err) {
      toast.error(err.message || 'Failed to complete bill');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePendingBill = async (billId) => {
    try {
      const res = await fetch(`${SALON_API_URL}/pending-bills/${billId}`, { method: 'DELETE', headers });
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      toast.success(data.message);
      setPendingBills(prev => prev.filter(b => b.id !== billId));
      await loadData();
    } catch (err) {
      toast.error(err.message || 'Failed to delete bill');
    }
  };

  const canNext = step === 1 ? cartItems.some(i=>i.item_type==='service') : true;
  
  const totalSteps = 2;
  const detailsStep = 2;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 size={32} className="animate-spin text-purple-600"/>
      </div>
    );
  }

  // ── List View: Show stations and pending bills ────────────────────────
  if (view === 'list') {
    return (
      <div className="p-4 sm:p-6 min-h-screen bg-gray-50 dark:bg-slate-900">
        <ToastContainer position="top-right" autoClose={3000} theme="colored"/>

        <div className="max-w-none">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-black text-gray-900 dark:text-white flex items-center gap-2">
                <ReceiptText size={22} className="text-purple-600"/> Billing
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                {pendingBills.length} bill{pendingBills.length !== 1 ? 's' : ''} on hold
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setStationModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-200 font-semibold text-sm border border-gray-200 dark:border-slate-700 transition-all"
              >
                <Settings size={16}/> Manage Workspaces
              </button>
              <button
                onClick={handleNewBill}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-bold text-sm shadow-lg transition-all"
              >
                <Plus size={16}/> New Bill (No Workspace)
              </button>
            </div>
          </div>

          {/* Workspaces Grid - Grouped by Type */}
          {stations.length > 0 && (
            <div className="mb-8">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Grid3x3 size={18} className="text-purple-600"/> Select Workspace to Start Bill
              </h2>
              
              {/* Group stations by type */}
              {['chair', 'shampoo', 'manipedi', 'room'].map(type => {
                const typeStations = stations.filter(s => s.station_type === type);
                if (typeStations.length === 0) return null;
                
                const TypeIcon = STATION_ICONS[type];
                const typeLabel = (STATION_LABELS[type] || type) + 's';
                
                return (
                  <div key={type} className="mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <TypeIcon size={16} className="text-gray-500"/>
                      <h3 className="text-sm font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wide">{typeLabel}</h3>
                      <div className="flex-1 h-px bg-gray-200 dark:bg-slate-700"></div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                      {typeStations.map(station => {
                        const StationIcon = STATION_ICONS[station.station_type] || Armchair;
                        const pendingBill = pendingBills.find(b => b.station_id === station.id);
                        const hasPendingBill = !!pendingBill;
                        
                        // Calculate bill total if exists
                        let subtotal = 0;
                        let billTotal = 0;
                        let itemCount = 0;
                        if (pendingBill) {
                          subtotal = pendingBill.items.reduce((s,i)=>s+(i.price*(i.qty||1)),0);
                          billTotal = Math.max(0, subtotal - (pendingBill.discount||0));
                          itemCount = pendingBill.items.length;
                        }
                        
                        return (
                          <div
                            key={station.id}
                            role="button"
                            tabIndex={0}
                            onClick={() => {
                              if (hasPendingBill) {
                                handleEditBill(pendingBill);
                              } else {
                                handleNewBill(station.id);
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                if (hasPendingBill) handleEditBill(pendingBill);
                                else handleNewBill(station.id);
                              }
                            }}
                            className={`relative flex flex-col gap-3 p-4 rounded-2xl border-2 transition-all hover:scale-105 cursor-pointer ${
                              hasPendingBill
                                ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-400 shadow-lg'
                                : 'bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 hover:border-purple-300'
                            }`}
                          >                           {hasPendingBill && (
                              <div className="absolute top-2 right-2 w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                            )}
                            
                            <div className="flex flex-col items-center gap-2">
                              <StationIcon size={32} className={hasPendingBill ? 'text-purple-600' : 'text-gray-400'}/>
                              <p className={`text-sm font-bold ${hasPendingBill ? 'text-purple-700 dark:text-purple-300' : 'text-gray-800 dark:text-white'}`}>
                                {station.name}
                              </p>
                            </div>
                            
                            {hasPendingBill ? (
                              <div className="w-full space-y-2 pt-2 border-t border-purple-200 dark:border-purple-700">
                                <div className="flex items-center justify-between">
                                  <span className="text-xs text-gray-500 dark:text-gray-400">Bill Total</span>
                                  <span className="flex items-baseline gap-1.5 text-right">
                                    {(pendingBill.discount || 0) > 0 && (
                                      <span className="text-xs font-bold text-gray-400 line-through">{fmt(subtotal)}</span>
                                    )}
                                    <span className="text-sm font-black text-green-600 dark:text-green-400">{fmt(billTotal)}</span>
                                  </span>
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className="text-xs text-gray-500 dark:text-gray-400">Items</span>
                                  <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">{itemCount}</span>
                                </div>
                                {pendingBill.customer_name && (
                                  <div className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
                                    <User size={10}/>
                                    <span className="truncate">{pendingBill.customer_name}</span>
                                  </div>
                                )}
                                <div className="flex items-center gap-1 text-xs text-gray-400">
                                  <Clock size={10}/>
                                  <span>{new Date(pendingBill.created_at).toLocaleTimeString()}</span>
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleCompletePendingBill(pendingBill.id);
                                    }}
                                    disabled={saving}
                                    className="flex items-center justify-center gap-1 rounded-lg bg-green-500 hover:bg-green-600 text-white text-xs font-bold py-1.5 disabled:opacity-60"
                                  >
                                    <CheckCircle size={12}/> Complete
                                  </button>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeletePendingBill(pendingBill.id);
                                    }}
                                    disabled={saving}
                                    className="flex items-center justify-center gap-1 rounded-lg bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 text-xs font-bold py-1.5 disabled:opacity-60"
                                  >
                                    <Trash2 size={12}/> Delete
                                  </button>
                                </div>
                                <span className="inline-block w-full text-center text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-2 py-1 rounded-lg font-semibold">
                                  Click card to edit
                                </span>
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400 text-center">Available</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Bills without workspace (No workspace assigned) */}
          {pendingBills.filter(b => !b.station_id).length > 0 && (
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Clock size={18} className="text-purple-600"/> Bills On Hold (No Workspace)
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {pendingBills.filter(b => !b.station_id).map(bill => {
                  const subtotal = bill.items.reduce((s,i)=>s+(i.price*(i.qty||1)),0);
                  const total = Math.max(0, subtotal - (bill.discount||0));
                  const station = stations.find(s=>s.id===bill.station_id);
                  const StationIcon = station ? STATION_ICONS[station.station_type] : null;

                  return (
                    <div key={bill.id} className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 overflow-hidden hover:shadow-lg transition-all">
                      <div className="p-4 border-b border-gray-100 dark:border-slate-700">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="flex-1">
                            {station && (
                              <div className="flex items-center gap-1.5 text-purple-600 mb-1">
                                {StationIcon && <StationIcon size={14}/>}
                                <span className="text-xs font-bold">{station.name}</span>
                              </div>
                            )}
                            {bill.customer_name && (
                              <p className="text-sm font-semibold text-gray-800 dark:text-white">{bill.customer_name}</p>
                            )}
                            {bill.customer_phone && (
                              <p className="text-xs text-gray-400 flex items-center gap-1"><Phone size={10}/>{bill.customer_phone}</p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-black text-green-600 dark:text-green-400">
                              {bill.discount > 0 && <span className="mr-1 text-xs font-bold text-gray-400 line-through">{fmt(subtotal)}</span>}
                              {fmt(total)}
                            </p>
                            <p className="text-xs text-gray-400">{bill.items.length} item{bill.items.length>1?'s':''}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 text-xs text-gray-400">
                          <Clock size={10}/>
                          <span>{new Date(bill.created_at).toLocaleString()}</span>
                        </div>
                      </div>

                      <div className="p-3 space-y-2">
                        <button
                          onClick={() => handleCompletePendingBill(bill.id)}
                          disabled={saving}
                          className="w-full flex items-center justify-center gap-2 py-2 rounded-xl bg-green-500 hover:bg-green-600 text-white font-bold text-sm transition-all disabled:opacity-60"
                        >
                          <CheckCircle size={14}/> Complete
                        </button>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEditBill(bill)}
                            className="flex-1 flex items-center justify-center gap-2 py-2 rounded-xl bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 text-gray-700 dark:text-gray-200 font-semibold text-sm transition-all"
                          >
                            <Edit2 size={14}/> Edit
                          </button>
                          <button
                            onClick={() => handleDeletePendingBill(bill.id)}
                            className="flex-1 flex items-center justify-center gap-2 py-2 rounded-xl bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 font-semibold text-sm transition-all"
                          >
                            <Trash2 size={14}/> Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Empty state */}
          {stations.length === 0 && pendingBills.length === 0 && (
            <div className="text-center py-20">
              <ReceiptText size={48} className="mx-auto text-gray-200 dark:text-slate-700 mb-4"/>
              <p className="text-lg font-semibold text-gray-400 mb-2">No workspaces or bills yet</p>
              <p className="text-sm text-gray-400 mb-6">Add workspaces or create your first bill</p>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={() => setStationModalOpen(true)}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-200 font-bold border border-gray-200 dark:border-slate-700 transition-all"
                >
                  <Settings size={18}/> Add Workspaces
                </button>
                <button
                  onClick={handleNewBill}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-bold shadow-lg transition-all"
                >
                  <Plus size={18}/> Create First Bill
                </button>
              </div>
            </div>
          )}
        </div>

        <StationModal
          isOpen={stationModalOpen}
          onClose={() => setStationModalOpen(false)}
          stations={stations}
          onSave={handleSaveStation}
          onDelete={handleDeleteStation}
        />
      </div>
    );
  }

  // ── Create/Edit View: 3-step wizard with right sidebar ────────────────
  return (
    <div className="flex h-screen bg-gray-50 dark:bg-slate-900">
      <ToastContainer position="top-right" autoClose={3000} theme="colored"/>

      {/* Left: Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-900">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setView('list')}
              className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-600 dark:text-gray-400"
            >
              <ChevronLeft size={20}/>
            </button>
            <div>
              <h1 className="text-xl font-black text-gray-900 dark:text-white flex items-center gap-2">
                <ReceiptText size={20} className="text-purple-600"/> {currentBillId ? 'Edit Bill' : 'New Bill'}
              </h1>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Step {step} of {totalSteps} · {cartItems.length} item{cartItems.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Workspace selector */}
            {stations.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">Workspace:</span>
                <select
                  value={selectedStation || ''}
                  onChange={e => setSelectedStation(e.target.value ? parseInt(e.target.value) : null)}
                  className="px-3 py-1.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-sm text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">None</option>
                  {stations.map(s => (
                    <option key={s.id} value={s.id}>{s.name} ({s.station_type})</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="max-w-3xl mx-auto lg:max-w-none" style={{ maxWidth: '85rem' }}>
            {step === 1 && (
              <div className="space-y-6">
                <section className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-3xl p-4 sm:p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <Scissors size={16} className="text-purple-600"/>
                    <h2 className="text-sm font-black text-gray-900 dark:text-white uppercase tracking-wide">Services</h2>
                  </div>
                  <div className="max-h-[58vh] overflow-y-auto pr-1">
                    <Step1Services cartItems={cartItems} onAdd={addItem} onRemove={removeItem} onOpenPackageBuilder={() => openPackageBuilder()}/>
                  </div>
                </section>
                <section className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-3xl p-4 sm:p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <Package size={16} className="text-blue-600"/>
                    <h2 className="text-sm font-black text-gray-900 dark:text-white uppercase tracking-wide">Products</h2>
                  </div>
                  <div className="max-h-[42vh] overflow-y-auto pr-1">
                    <Step2Products cartItems={cartItems} onAdd={addItem} onUpdateQty={updateProductQty} onRemove={removeItem}/>
                  </div>
                </section>
              </div>
            )}
            {step === detailsStep && <Step3Details cartItems={cartItems} staffList={staffList} billDetails={billDetails} onChange={setBillDetails} customerSuggestions={customerSuggestions}/> }
          </div>
        </div>

        {/* Bottom nav - Fixed at bottom */}
        <div className="bg-white dark:bg-slate-900 border-t border-gray-200 dark:border-slate-800 p-4 sticky bottom-0">
          <div className="max-w-3xl mx-auto lg:max-w-none flex gap-3" style={{ maxWidth: '85rem' }}>
            <button
              onClick={() => step > 1 && setStep(s => s - 1)}
              disabled={step === 1}
              className={`flex items-center gap-2 px-5 py-3 rounded-xl border text-sm font-semibold transition-all
                ${step === 1
                  ? 'border-gray-100 dark:border-slate-800 text-gray-300 dark:text-slate-700 cursor-not-allowed'
                  : 'border-gray-200 dark:border-slate-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-800 active:scale-95'
                }`}
            >
              <ChevronLeft size={16}/> Back
            </button>

            {step < detailsStep && (
              <button
                onClick={() => setStep(s => s + 1)}
                disabled={!canNext}
                className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold transition-all active:scale-95
                  ${canNext
                    ? 'bg-purple-600 hover:bg-purple-700 text-white shadow-lg'
                    : 'bg-gray-200 dark:bg-slate-700 text-gray-400 cursor-not-allowed'
                  }`}
              >
                {step === 1 && !canNext ? (
                  <span className="text-xs">Select at least 1 service</span>
                ) : (
                  <>
                    Continue to Details
                    <ChevronRight size={16}/>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      <PackageBuilderModal
        isOpen={packageBuilderOpen}
        packageItem={editingPackageItem}
        onClose={() => { setPackageBuilderOpen(false); setEditingPackageItem(null); }}
        onSave={handleAddPackage}
      />

      {/* Right: Bill Summary */}
      <div className="hidden lg:block relative flex-shrink-0" style={{ width: summaryWidth }}>
        <BillSummary
          cartItems={cartItems}
          billDetails={billDetails}
          staffList={staffList}
          customerSuggestions={customerSuggestions}
          onComplete={handleCompleteBill}
          onHold={handleHoldBill}
          saving={saving}
          onPaymentChange={(method) => setBillDetails({...billDetails, payment_method: method})}
          onRemoveItem={removeItem}
          onDiscountChange={setBillDetails}
          onResizeStart={startSummaryResize}
          onOpenPackageBuilder={() => openPackageBuilder()}
          onEditPackage={openPackageBuilder}
          onUpdateItem={updateCartItem}
          onUpdateItemQty={updateItemQty}
        />
      </div>
    </div>
  );
}
